import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import {
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import { Field, Form, Formik } from "formik";
import { awsImageRootUrl } from "lib/constants";
import { Category, fetchCategory } from "lib/features/category/categoryService";
import { useGetIndustryTypesQuery } from "lib/features/industries/industriesService";
import { Industry } from "lib/features/industries/types";
import {
  CreateAdditionalOptionDto,
  CreateItemWithDetailDto,
} from "lib/features/item/types";
import { useEffect, useState } from "react";
import { formatDuration } from "util/commonHelper";

interface ReviewStepProp {
  initialValues: CreateItemWithDetailDto;
  onSubmit: (values: CreateItemWithDetailDto) => void;
  loader: boolean;
}

interface InfoContainerProp {
  title: string;
  content: any;
}

const InfoContainer = ({ title, content }: InfoContainerProp) => {
  return (
    <Box>
      <Typography
        sx={{ fontSize: "20px", fontWeight: "600", color: "#1C1B1C" }}
      >
        {title}
      </Typography>
      <Typography
        sx={{
          fontSize: "15px",
          fontWeight: "400",
          color: "#1C1B1C",
          paddingTop: "2px",
        }}
      >
        {content}
      </Typography>
    </Box>
  );
};

const InfoContainerOfSellingPoint = ({ title, content }: InfoContainerProp) => {
  return (
    <Box>
      <Typography
        sx={{ fontSize: "20px", fontWeight: "600", color: "#1C1B1C" }}
      >
        {title}
      </Typography>
      <Box sx={{ paddingTop: "2px" }}>
        {content?.split("\n").map((line: any, index: any) => (
          <Typography
            key={index}
            sx={{
              fontSize: "15px",
              fontWeight: "400",
              color: "#1C1B1C",
            }}
          >
            {line}
          </Typography>
        ))}
      </Box>
    </Box>
  );
};

const ReviewStep = ({ initialValues, onSubmit, loader }: ReviewStepProp) => {
  const theme = useTheme();

  const { data: industries } = useGetIndustryTypesQuery(null, {});
  const [categories, setCategories] = useState<Category[]>([]);

  useEffect(() => {
    const getCategories = async () => {
      try {
        const categories = await fetchCategory();
        setCategories(categories);
      } catch (error) {}
    };

    getCategories();
  }, []);

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={(values) => onSubmit(values)}
    >
      {({ values, errors, touched, setFieldValue }) => {
        const commissionRateDisplay = () => {
          const { comission, price } = values;

          const calculatedCommission = (price * comission) / 100;
          const formattedPercentage = `${comission}% ($${calculatedCommission.toFixed(
            2
          )})`;
          return formattedPercentage;
        };

        return (
          <Form>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: "15px",
                fontFamily: theme.typography.fontFamily,
              }}
            >
              <InfoContainer title="Item Name" content={values?.item_name} />

              <InfoContainer title="Headline" content={values?.headline} />

              {/* Photos */}
              <Typography
                sx={{ fontSize: "20px", fontWeight: "600", color: "#1C1B1C" }}
              >
                Item Photos
              </Typography>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "repeat(2, 1fr)",
                  gap: "1rem 1rem",
                  marginTop: "1rem",
                  marginBottom: "1.5rem",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                {values.images &&
                  values.images.length > 0 &&
                  values.images.map((image: any) => {
                    const imageUrl = `${awsImageRootUrl}/${image}`;
                    return (
                      <img
                        key={imageUrl}
                        src={imageUrl}
                        alt="Uploaded"
                        style={{
                          objectFit: "cover",
                          width: "100%",
                          height: "11rem",
                          borderRadius: "0.5rem",
                        }}
                      />
                    );
                  })}
              </Box>

              <InfoContainerOfSellingPoint
                title="Selling Points and Important Info"
                content={values?.selling_points}
              />

              {/* Industries */}
              <Box>
                <Typography
                  sx={{ fontSize: "20px", fontWeight: "600", color: "#1C1B1C" }}
                >
                  Industries
                </Typography>
                <Box display="flex" flexWrap="wrap" gap={1}>
                  {industries &&
                    industries?.map((industryType: Industry) => {
                      const _selected = values.industry_ids.includes(
                        industryType?.id
                      );

                      return (
                        <Chip
                          key={industryType.id}
                          label={industryType.industry}
                          variant={_selected ? "filled" : "outlined"}
                          color={_selected ? "primary" : "default"}
                          sx={{
                            marginTop: "5px",
                            border: _selected ? "none" : "1px solid #000",
                          }}
                        />
                      );
                    })}
                </Box>
              </Box>

              {/* Categories */}
              {values?.industry_ids?.includes(1) &&
                industries &&
                industries?.length > 0 && (
                  <Box>
                    <Typography
                      sx={{
                        fontSize: "20px",
                        fontWeight: "600",
                        color: "#1C1B1C",
                      }}
                    >
                      {industries[0]?.industry} Categories
                    </Typography>
                    <Box display="flex" flexWrap="wrap" gap={1}>
                      {categories &&
                        categories
                          ?.filter(
                            (item: Category) => item?.category_type === 1
                          )
                          ?.map((category: Category) => {
                            const _selected = values.category_ids.includes(
                              category?.id
                            );
                            return (
                              <Chip
                                key={category.id}
                                label={category.category}
                                variant={_selected ? "filled" : "outlined"}
                                color={_selected ? "primary" : "default"}
                                sx={{
                                  marginTop: "5px",
                                  border: _selected ? "none" : "1px solid #000",
                                }}
                              />
                            );
                          })}
                    </Box>
                    {errors.industry_ids && touched.industry_ids && (
                      <Typography color="red" fontSize="12px">
                        {errors.industry_ids}
                      </Typography>
                    )}
                  </Box>
                )}

              {values?.industry_ids?.includes(2) &&
                industries &&
                industries?.length > 1 && (
                  <Box>
                    <Typography
                      sx={{
                        fontSize: "20px",
                        fontWeight: "600",
                        color: "#1C1B1C",
                      }}
                    >
                      {industries[1]?.industry} Categories
                    </Typography>
                    <Box display="flex" flexWrap="wrap" gap={1}>
                      {categories &&
                        categories
                          ?.filter(
                            (item: Category) => item?.category_type === 1
                          )
                          ?.map((category: Category) => {
                            const _selected = values.category_ids.includes(
                              category?.id
                            );
                            return (
                              <Chip
                                key={category.id}
                                label={category.category}
                                variant={_selected ? "filled" : "outlined"}
                                color={_selected ? "primary" : "default"}
                                sx={{
                                  marginTop: "5px",
                                  border: _selected ? "none" : "1px solid #000",
                                }}
                              />
                            );
                          })}
                    </Box>
                    {errors.industry_ids && touched.industry_ids && (
                      <Typography color="red" fontSize="12px">
                        {errors.industry_ids}
                      </Typography>
                    )}
                  </Box>
                )}

              <InfoContainer
                title="Description"
                content={values?.description}
              />

              <InfoContainer
                title="Duration"
                content={formatDuration(
                  values.duration_hours,
                  values.duration_minutes
                )}
              />

              <InfoContainer
                title="What’s Included"
                content={values?.whats_included}
              />

              <InfoContainer
                title="Additional Info"
                content={values?.additional_info}
              />

              <InfoContainer
                title="Max Number of Guests"
                content={values?.max_guest}
              />

              <InfoContainer title="Minimum Age" content={values?.min_age} />

              <InfoContainer title="Maximum Age" content={values?.max_age} />

              <InfoContainer
                title="Cancellation Policy"
                content={values?.cancellation_policy}
              />

              <InfoContainer title="Base Price" content={values?.price} />

              <InfoContainer title="# Available" content={values?.capacity} />

              <InfoContainer
                title="Pricing Description"
                content={values?.price_description}
              />

              <InfoContainer title="Unit Name" content={values?.unit_name} />

              <InfoContainer
                title="Commission Rate"
                content={commissionRateDisplay()}
              />

              <InfoContainer title="Unit Name" content={values?.unit_name} />

              <InfoContainer
                title="Customer Added Value"
                content={values?.offering}
              />

              {/* Additional Information */}
              <Box>
                {values?.additionalOptions &&
                  values?.additionalOptions?.length > 0 &&
                  values?.additionalOptions?.map(
                    (item: CreateAdditionalOptionDto, index: any) => (
                      <Box
                        key={index}
                        sx={{
                          // gap: "7px",
                          // padding: "10px",
                          // borderRadius: "8px",
                          marginY: "15px",
                        }}
                      >
                        {/* Header */}
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "row",
                            justifyContent: "space-between",
                            alignItems: "center",
                          }}
                        >
                          <Typography
                            sx={{
                              fontSize: "17px",
                              color: "#000",
                              fontWeight: "700",
                            }}
                          >
                            {item.type === 1
                              ? "Text Field"
                              : item.type === 2
                              ? "Quantity Dropdown"
                              : "Check Box"}
                          </Typography>
                        </Box>

                        {/* Body */}
                        {item?.type === 1 ? (
                          <>
                            <Typography
                              sx={{
                                marginTop: "0px",
                                fontSize: "17px",
                                fontWeight: "600",
                                paddingY: "5px",
                              }}
                            >
                              {item?.header}
                              {item?.required && "*"}
                            </Typography>
                            <Field
                              as={TextField}
                              name="place_holder"
                              placeholder={item?.place_holder ?? ""}
                              fullWidth
                              InputProps={{
                                readOnly: true,
                              }}
                            />
                          </>
                        ) : item?.type === 2 ? (
                          <>
                            <Typography
                              sx={{
                                marginTop: "0px",
                                fontSize: "17px",
                                fontWeight: "600",
                                paddingY: "5px",
                              }}
                            >
                              {item?.header}
                              {item?.required && "*"}
                            </Typography>

                            <Box
                              sx={{
                                display: "flex",
                                flexDirection: "row",
                                alignItems: "center",
                                border: "1px solid #000",
                                borderRadius: "8px",
                                padding: "15px",
                                justifyContent: "space-between",
                              }}
                            >
                              <Typography
                                sx={{
                                  fontSize: "16px",
                                  color: "#000",
                                  fontWeight: "400",
                                }}
                              >
                                {item?.availability}
                              </Typography>
                              <Box
                                sx={{
                                  display: "flex",
                                  flexDirection: "row",
                                  gap: "20px",
                                  alignItems: "center",
                                }}
                              >
                                {/* Price */}
                                <Typography
                                  sx={{
                                    fontSize: "16px",
                                    color: "#000",
                                    fontWeight: "600",
                                  }}
                                >
                                  ${item?.price}
                                </Typography>

                                <ArrowDropDownIcon />
                              </Box>
                            </Box>
                          </>
                        ) : (
                          <Box
                            sx={{
                              border: "1px solid #E0E0E0",
                              borderRadius: "8px",
                              padding: "10px",
                              display: "flex",
                              flexDirection: "row",
                              alignItems: "center",
                              marginBottom: "10px",
                            }}
                          >
                            {/* Left side: Checkbox */}
                            <Box sx={{ marginRight: "10px" }}>
                              <Checkbox name="isRequired" />
                            </Box>

                            {/* Middle section: Content */}
                            <Box sx={{ flex: 1 }}>
                              <Box
                                sx={{
                                  display: "flex",
                                  flexDirection: "row",
                                  justifyContent: "space-between",
                                }}
                              >
                                <Typography
                                  sx={{
                                    marginTop: "0px",
                                    fontSize: "17px",
                                    fontWeight: "600",
                                  }}
                                >
                                  {item?.header}
                                  {item?.required && "*"}
                                </Typography>
                                <Typography
                                  sx={{
                                    marginTop: "0px",
                                    fontSize: "17px",
                                    fontWeight: "700",
                                  }}
                                >
                                  ${item?.price}
                                </Typography>
                              </Box>

                              <Typography
                                sx={{
                                  fontSize: "15px",
                                  color: "#595959",
                                  marginTop: 4,
                                  fontWeight: "400",
                                }}
                              >
                                {item?.description}
                              </Typography>
                            </Box>
                          </Box>
                        )}
                      </Box>
                    )
                  )}
              </Box>

              <Button
                type="submit"
                variant="contained"
                sx={{ width: "100%", marginTop: "20px", paddingY: "10px" }}
              >
                {loader ? (
                  <CircularProgress size={24} sx={{ color: "white" }} />
                ) : (
                  "Continue"
                )}
              </Button>
            </Box>
          </Form>
        );
      }}
    </Formik>
  );
};

export default ReviewStep;
